import {
  type DataGridPremiumProps,
  GRID_TREE_DATA_GROUPING_FIELD,
  type GridCallbackDetails,
  type GridFilterModel,
  type GridGroupNode,
  type GridInitialState,
  type GridPaginationModel,
  type GridSortModel,
  type GridValidRowModel,
  useGridApiRef,
} from "@mui/x-data-grid-premium";
import { useDataRouterContext } from "src/lib/react-router/useDataRouterContext.ts";
import zod from "zod";
import { useContentLocale } from "src/auth/hooks/useContentLocale.tsx";
import {
  keepPreviousData,
  type UseQueryOptions,
  useSuspenseQuery,
} from "@tanstack/react-query";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  mapGridStateToPersistedState,
  type PersistedGridState,
} from "src/components/DataGrid/utils/mapGridStateToPersistedState.tsx";
import { useUrlState } from "@exonn/use-url-state";
import { deepEq, mapObjectToArray, removeItem, unique } from "@exonn/utils";

import type { GridEventListener } from "@mui/x-data-grid";
import type { DataGridProps } from "src/components/DataGrid/DataGrid.tsx";

import { useList } from "src/system/useList.tsx";
import type { ValidGetListResponse } from "src/system/SystemProvider.tsx";
import { useResourceStorageKeys } from "src/framework/useResourceStorageKeys.tsx";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useSnackbar } from "src/App/App.hooks.ts";
import { logger } from "src/logger";
import { useUser } from "src/auth/auth.ts";
import { useUserDataState } from "src/user-data/useUserDataState.ts";
import {
  DataGridExpandedRowsSchema,
  DataGridPersistedStateSchema,
} from "src/user-data/schemas.ts";
import type { DataGridExpandedRowsPersistedState } from "src/user-data/types.ts";
import { useGlobalSettings } from "src/hooks/use-global-settings.ts";

import { useProductGridProps } from "src/system/useProductGridProps.tsx";
import { useB2bGateOrderGridProps } from "src/system/useB2bGateOrderProps.tsx";
import { getDataGridCustomMapper } from "src/system/getDataGridCustomMapper.ts";

import { useSearchParams } from "react-router-dom";
import { ResourceEnum } from "src/constants/constants.ts";
import { useClientConfig } from "src/client-config/useClientConfig.ts";
import { resourceListConfigQuery } from "src/system/resourceListConfigQuery.ts";
import { listTranslationsQuery } from "src/system/listTranslationsQuery.ts";
import type { GridLogicOperator } from "@mui/x-data-grid-pro";
import type { GridSortDirection } from "@mui/x-data-grid/models/gridSortModel";
import { prepareFilterModel } from "src/system/prepareFilterModel.tsx";
import { mergeFilterModels } from "src/components/DataGrid/mergeFilterModels.ts";
import { getResourceI18nNamespace } from "src/system/getResourceI18nNamespace.ts";
import { SelectV2 } from "src/components/SelectV2/SelectV2.tsx";
import { useErpTranslation } from "src/system/useErpTranslation.ts";

export enum ResourceListSearchParams {
  SortModel = "s",
  Page = "page",
  PageSize = "pageSize",
  FilterModel = "f",
  NotAppliedFilterModel = "nf",
  FilterModelLogicOperator = "fl",
  Option = "option",
  DataLoadingEnabledByForce = "dataLoadingEnabledByForce",
}

type UrlSortModel = Record<string, GridSortDirection>;

export type UrlFilterModel = Record<
  string,
  {
    v: any;
    o: string;
  }
>;

type UrlState = {
  [ResourceListSearchParams.SortModel]?: UrlSortModel;
  [ResourceListSearchParams.Page]?: number;
  [ResourceListSearchParams.PageSize]?: number;
  [ResourceListSearchParams.NotAppliedFilterModel]?: UrlFilterModel;
  [ResourceListSearchParams.FilterModel]?: UrlFilterModel;
  [ResourceListSearchParams.FilterModelLogicOperator]?: GridLogicOperator;
  [ResourceListSearchParams.DataLoadingEnabledByForce]?: boolean;
};

const urlStateValidationSchema = zod.object({
  [ResourceListSearchParams.FilterModelLogicOperator]: zod.string().optional(),
  [ResourceListSearchParams.FilterModel]: zod
    .record(
      zod.string(),
      zod.object({
        v: zod.any(),
        o: zod.string(),
      }),
    )
    .optional(),
  [ResourceListSearchParams.NotAppliedFilterModel]: zod
    .record(
      zod.string(),
      zod.object({
        v: zod.any(),
        o: zod.string(),
      }),
    )
    .optional(),
  [ResourceListSearchParams.SortModel]: zod
    .record(zod.string(), zod.string().nullable())
    .optional(),
  [ResourceListSearchParams.PageSize]: zod.number().optional(),
  [ResourceListSearchParams.Page]: zod.number().optional(),
  [ResourceListSearchParams.DataLoadingEnabledByForce]: zod
    .boolean()
    .optional(),
});

const DEVELOPER_COLUMNS = ["id"];

export type UseResourceListProps<
  Row extends GridValidRowModel = GridValidRowModel,
> = {
  resource: string;
  filter?: GridFilterModel;
  include?: string[];
  disableStatePersistenceToUrl?: boolean;
  expandRowOnRowClick?: boolean;
  withoutDescendants?: boolean;

  queryOptions?: Omit<
    UseQueryOptions<
      ValidGetListResponse<Row>,
      any,
      ValidGetListResponse<Row>,
      any
    >,
    "queryFn" | "queryKey"
  >;
  saveToDatabase?: boolean;

  disableAutomaticDataLoading?: boolean;
} & Partial<DataGridProps<Row>>;

export const useResourceList = <
  Row extends GridValidRowModel = GridValidRowModel,
>(
  props: UseResourceListProps<Row>,
) => {
  const {
    disableAutomaticDataLoading = false,
    disableStatePersistenceToUrl = false,
    loading: loadingProp,
    withoutDescendants = false,
    expandRowOnRowClick = false,
    resource,
    include: includeProp,
    filter: staticFilter,
    saveToDatabase = true,
    onToolbarRefresh: onToolbarRefreshProp,
    apiRef: apiRefProp,
    queryOptions,
    onRowClick,
    ...restProps
  } = props;
  const { router } = useDataRouterContext();

  const pendingUrlState = useRef<UrlState>();

  const { data: user } = useUser();
  const snackbar = useSnackbar();
  const { data: globalSettings } = useGlobalSettings();

  const [searchParams, setSearchParams] = useSearchParams();

  const value = searchParams.get("option") ?? "default";
  const handleSelect = (value: string) => {
    if (value === "default") {
      searchParams.delete("option");
      setSearchParams(searchParams);
    } else {
      searchParams.set("option", value);
      setSearchParams(searchParams);
    }
  };
  const option = searchParams.get("option") ?? "default";

  const { t } = useErpTranslation({
    resource,
    type: "list",
  });

  const clientConfig = useClientConfig();
  const { data: listConfig, refetch: refetchListConfig } = useSuspenseQuery({
    ...resourceListConfigQuery({
      resource,
      clientConfig,
    }),
  });

  const {
    getColumns,
    mapResourceToRow,
    toolbarStartAdditionalElements: toolbarStartAdditionalElementsProp = [],
    includes: includesFromConfig,
  } = listConfig;

  const { refetch: refetchTranslations } = useSuspenseQuery(
    listTranslationsQuery({
      resource,
    }),
  );

  const contentLocale = useContentLocale()!;

  const localApiRef = useGridApiRef();
  const apiRef = apiRefProp || localApiRef;

  const storageKeys = useResourceStorageKeys({
    resource,
  });

  const {
    setValue: setUserDataExpandedRowsState,
    clear: clearExpandedRowsState,
    getValue: getUserExpandedRowsState,
  } = useUserDataState<DataGridExpandedRowsPersistedState>({
    user: user!,
    validationSchema: DataGridExpandedRowsSchema,
    key: storageKeys.listExpandedRows,
    disableSavingToDatabase: true,
  });

  const { value: userDataState, setValue: setUserDataState } =
    useUserDataState<PersistedGridState>({
      user: user!,
      key: storageKeys.listState,
      validationSchema: DataGridPersistedStateSchema,
      disableSavingToDatabase: !saveToDatabase,
    });

  const initialState = hideIdColumnInInitialState(userDataState);

  const [gridUrlState, setGridUrlState] = useUrlState<UrlState>(
    router,
    {
      [ResourceListSearchParams.FilterModel]: {},
      [ResourceListSearchParams.PageSize]: 0,
      [ResourceListSearchParams.Page]: 0,
      [ResourceListSearchParams.SortModel]: {},
    },
    {
      disablePersistence: disableStatePersistenceToUrl,
      validationSchema: urlStateValidationSchema,
    },
  );

  const {
    filterModel: appliedFilterModel,
    paginationModel,
    sortModel,
    notAppliedFilterModel,
  } = convertUrlStateToGridState(gridUrlState);

  const setFilterModel = (model: GridFilterModel) => {
    const modelWithoutIds = filterModelWithoutIds(model);

    if (deepEq(notAppliedFilterModel, model)) {
      return;
    }

    const nextUrlState = {
      [ResourceListSearchParams.NotAppliedFilterModel]:
        convertGridFilterModelToUrlFilterModel(modelWithoutIds),
    };
    pendingUrlState.current = {
      ...pendingUrlState.current,
      ...nextUrlState,
    };

    setGridUrlState({
      ...pendingUrlState.current,
    });
    setTimeout(() => {
      pendingUrlState.current = {};
    });
  };

  const setSortModel = (model: GridSortModel) => {
    clearExpandedRowsState();

    setGridUrlState(
      {
        [ResourceListSearchParams.SortModel]:
          convertGridSortModelToUrlSortModel(model),
      },
      {
        onFinish: () => {
          refetchData();
        },
      },
    );
  };

  const setAppliedFilterModel = (appliedFilterModel: GridFilterModel) => {
    clearExpandedRowsState();

    const nextUrlState = {
      [ResourceListSearchParams.Page]: 0,
      [ResourceListSearchParams.FilterModel]:
        convertGridFilterModelToUrlFilterModel(appliedFilterModel),
      [ResourceListSearchParams.FilterModelLogicOperator]:
        appliedFilterModel.logicOperator,
    };
    pendingUrlState.current = {
      ...pendingUrlState.current,
      ...nextUrlState,
    };
    refetchData();
    setGridUrlState({
      ...pendingUrlState.current,
    });
    setTimeout(() => {
      pendingUrlState.current = {};
    });
  };

  const setPaginationModel = (
    model: GridPaginationModel,
    details: GridCallbackDetails,
  ) => {
    if (details.reason !== "autoPageSize") {
      clearExpandedRowsState();
    }

    const nextPage = Math.max(0, model.page);
    const nextPageSize = Math.max(model.pageSize, 0);
    const prevPage = gridUrlState[ResourceListSearchParams.Page];
    const prevPageSize = gridUrlState[ResourceListSearchParams.PageSize];

    if (nextPage === prevPage && prevPageSize === nextPageSize) {
      return;
    }
    setGridUrlState(
      {
        [ResourceListSearchParams.Page]: Math.max(0, model.page),
        [ResourceListSearchParams.PageSize]: Math.max(model.pageSize, 0),
      },
      {
        onFinish: () => {
          refetchData();
        },
      },
    );
  };

  const dataLoadingEnabledByForce =
    gridUrlState[ResourceListSearchParams.DataLoadingEnabledByForce];

  const dataLoadingEnabled =
    !!paginationModel.pageSize &&
    (dataLoadingEnabledByForce || !disableAutomaticDataLoading);

  const combinedInclude = unique([
    ...(includeProp || []),
    ...(includesFromConfig || []),
  ]);

  const queryResult = useList<Row>({
    resource: props.resource,
    locale: contentLocale,
    invalidatePreviousQuery: true,
    include: combinedInclude,

    filter: prepareFilterModel(
      {
        ...appliedFilterModel,
        items: [...appliedFilterModel.items, ...(staticFilter?.items || [])],
      },
      getColumns({
        t,
        data: undefined,
        option,
        clientConfig,
        globalSettings,
      }),
    ),
    sort: sortModel,
    option,
    page: {
      number: paginationModel.page + 1,
      size: paginationModel.pageSize,
    },
    queryOptions: {
      structuralSharing: false,
      enabled: dataLoadingEnabled,
      staleTime: Infinity, // set to 'Infinity' to prevent query from automatically refetching on mount
      refetchOnMount: true,
      placeholderData: keepPreviousData,
      ...queryOptions,
    },
  });

  const {
    data: listData,
    isFetching,
    refetch: refetchData,
    error: listError,
  } = queryResult;

  const columns = useMemo(() => {
    const cols = getColumns({
      t: t,
      data: listData,
      globalSettings: globalSettings,
      option,
      clientConfig,
    }).map(({ headerName, field, ...restColumn }) => ({
      headerName: headerName ?? t(`table.columns.${field}`),
      field,
      ...restColumn,
    }));

    if (import.meta.env.DEV) {
      return cols;
    }
    return cols.filter((column) => !DEVELOPER_COLUMNS.includes(column.field));
  }, [getColumns, listData, t, option, globalSettings, clientConfig]);

  const handleRecoverFromError = () => {
    apiRef.current.setSortModel([]);
    apiRef.current.setPaginationModel({
      page: 0,
      pageSize: 0,
    });
    apiRef.current.setFilterModel({
      items: [],
    });
    setAppliedFilterModel({
      items: [],
    });

    refetchData();
  };

  const { rows, error: mapRowsError } = createMapRowsResult({
    mapResourceToRow:
      getDataGridCustomMapper({
        resource,
      }) || mapResourceToRow,
    data: listData,
    option,
  });

  const [showedErrors] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (mapRowsError && mapRowsError instanceof Error) {
      if (showedErrors.has(mapRowsError.message)) {
        return;
      }
      snackbar.error({
        flash: false,
        message: "Failed mapping list data: " + mapRowsError.message,
      });
      showedErrors.add(mapRowsError.message);
      logger.error(mapRowsError);
    }
  }, [mapRowsError, snackbar, showedErrors]);

  const hideToolbarPagination = !dataLoadingEnabled;

  const loading = isFetching || loadingProp;

  const handleToolbarRefresh = () => {
    clearExpandedRowsState();
    refetchData();
    const mergedModel = mergeFilterModels(
      notAppliedFilterModel,
      appliedFilterModel,
    );
    setAppliedFilterModel(mergedModel);
    setFilterModel({
      items: [],
    });

    onToolbarRefreshProp?.();
    if (import.meta.env.DEV) {
      refetchListConfig();
      refetchTranslations();
    }
  };

  const enableDataLoading = () => {
    setGridUrlState({
      [ResourceListSearchParams.DataLoadingEnabledByForce]: true,
    });
  };

  const isGroupExpandedByDefaultInternal = useCallback(
    (node: GridGroupNode) => {
      const stateFromStorage = getUserExpandedRowsState() || [];

      return stateFromStorage.includes(node.id);
    },
    [getUserExpandedRowsState],
  );

  const handleStateChange: DataGridPremiumProps<Row>["onStateChange"] = (
    state,
  ) => {
    const orderedFields = unique([
      // make sure grouping column always at the beginning
      // fixes https://link340.lcube-server.de/exonn-erp2/erp2-frontend/-/issues/88
      ...(props.treeData ? [GRID_TREE_DATA_GROUPING_FIELD] : []),
      ...(state?.columns?.orderedFields || []),
    ]);

    const finalState = {
      ...state,
      columns: {
        ...state.columns,
        orderedFields,
      },
    };
    setUserDataState(mapGridStateToPersistedState(finalState));
  };

  useEffect(() => {
    const onRowExpansionChange: GridEventListener<"rowExpansionChange"> = (
      params,
    ) => {
      const { childrenExpanded, id } = params;
      const prevExpanded = getUserExpandedRowsState() || [];

      const nextExpanded = childrenExpanded
        ? [...prevExpanded, id]
        : removeItem(prevExpanded, id);

      setUserDataExpandedRowsState(nextExpanded);
    };
    return apiRef.current?.subscribeEvent?.(
      "rowExpansionChange",
      onRowExpansionChange,
    );
  }, [apiRef, getUserExpandedRowsState, setUserDataExpandedRowsState]);

  const rowCount = listData ? listData.meta.total : 0;

  const productGridProps = useProductGridProps({
    resource,
    locale: contentLocale,
    withoutDescendants,
    apiRef,
    expandRowOnRowClick,
    onRowClick,
    appliedFilterModel: appliedFilterModel,
    includes: combinedInclude,
  });

  const b2bGateOrderGridProps = useB2bGateOrderGridProps({
    resource,
    locale: contentLocale,
    apiRef,
    expandRowOnRowClick,
    onRowClick,
    appliedFilterModel: appliedFilterModel,
    includes: combinedInclude,
  });

  const toolbarStartAdditionalElements = [
    ...(resource === ResourceEnum.Stock
      ? [
          <SelectV2
            isButton={true}
            value={value}
            options={[
              {
                value: "default",
                label: t("options.view1"),
              },
              {
                value: "warehouse-place",
                label: t("options.view2"),
              },
              {
                value: "warehouse",
                label: t("options.view3"),
              },
              {
                value: "owner",
                label: t("options.view4"),
              },
            ]}
            onChange={handleSelect}
            startAdornment={<i className={"icon-15-data_rprd"} />}
            selectContainerStyle={{ width: "200px" }}
          />,
        ]
      : []),
    ...toolbarStartAdditionalElementsProp,
  ];

  const dataGridProps: DataGridProps<Row> = {
    rowCount,
    sortingMode: "server",
    paginationMode: "server",
    filterMode: "server",
    rows: listError ? [] : rows,
    onRowClick,
    appliedFilterModel: appliedFilterModel,
    onAppliedFilterModelChange: setAppliedFilterModel,
    disableVirtualization: true,
    columns,
    loading,
    initialState,
    showNoInitialDataLoadedOverlay: disableAutomaticDataLoading,
    onLoadInitialData: enableDataLoading,
    onToolbarRefresh: handleToolbarRefresh,
    onStateChange: handleStateChange,
    toolbarStartAdditionalElements,
    isGroupExpandedByDefault: isGroupExpandedByDefaultInternal,
    filterModel: notAppliedFilterModel,
    sortModel: sortModel,
    noRowsOverlayTitle: t(
      `noRows`,
      t("noRows", {
        ns: TranslationNamespaceEnum.DataGridComponent,
      }),
      {
        ns: getResourceI18nNamespace(resource),
      },
    ),
    disableMultipleRowSelection: true,
    paginationModel: paginationModel,
    onPaginationModelChange: setPaginationModel,
    onSortModelChange: setSortModel,
    onFilterModelChange: setFilterModel,
    onRecoverFromError: handleRecoverFromError,
    error: listError,
    hideToolbarPagination,
    createToolbarNewRecordText: t(`new`, {
      ns: getResourceI18nNamespace(resource),
    }),

    hideToolbarCsvButton: true,

    apiRef,
    autoPageSize: true,
    ...productGridProps,
    ...b2bGateOrderGridProps,
    ...restProps,
  };

  return {
    dataGridProps,
    queryResult,
    key: option,
  };
};

type MapRowsResult<Row extends GridValidRowModel = GridValidRowModel> = {
  rows: Row[];
  error?: unknown;
};

const createMapRowsResult = <
  Row extends GridValidRowModel = GridValidRowModel,
>({
  data,
  option,
  mapResourceToRow,
}: {
  data: ValidGetListResponse<Row> | undefined;
  option: string;
  mapResourceToRow?: (
    data: any,
    params: {
      option?: string;
    },
  ) => Row;
}): MapRowsResult<Row> => {
  if (data && mapResourceToRow) {
    try {
      const rows = data.data.map((resource) => ({
        ...mapResourceToRow(resource, {
          option,
        }),
        _resource: resource,
      }));
      return {
        rows,
      };
    } catch (error: unknown) {
      return {
        rows: data.data.map((resource) => ({
          ...resource,
          _resource: resource,
        })), // falling back to raw data
        error,
      };
    }
  }
  if (data) {
    return {
      rows: data.data.map((resource) => ({
        ...resource,
        _resource: resource,
      })),
    };
  }

  return {
    rows: [],
  };
};

const hideIdColumnInInitialState = (
  initialState: GridInitialState | undefined,
) => {
  if (initialState?.columns?.columnVisibilityModel?.["id"] == null) {
    return {
      ...initialState,
      columns: {
        ...initialState?.columns,
        columnVisibilityModel: {
          ...initialState?.columns?.columnVisibilityModel,
          id: false,
        },
      },
    };
  }
  return initialState;
};

const convertGridFilterModelToUrlFilterModel = (
  model: GridFilterModel,
): UrlFilterModel => {
  return (model.items || [])
    .filter((item) => {
      if (Array.isArray(item.value)) {
        return item.value.length !== 0;
      }
      return item.value != null;
    })
    .reduce((acc, item) => {
      return {
        ...acc,
        [item.field]: {
          v: item.value,
          o: item.operator,
        },
      };
    }, {} as UrlFilterModel);
};

const convertGridSortModelToUrlSortModel = (model: GridSortModel) => {
  return model.reduce((acc, item) => {
    return {
      ...acc,
      [item.field]: item.sort,
    };
  }, {} as UrlSortModel);
};

const convertUrlStateToGridState = (gridUrlState: UrlState) => {
  const paginationModel = {
    page: gridUrlState[ResourceListSearchParams.Page] ?? 0,
    pageSize: gridUrlState[ResourceListSearchParams.PageSize] ?? 0,
  };
  const sortModelFromUrl = gridUrlState[ResourceListSearchParams.SortModel];
  const sortModel = mapObjectToArray(sortModelFromUrl || {}, (value, key) => {
    return {
      field: key,
      sort: value,
    };
  });

  const appliedFilterModelFromUrl =
    gridUrlState[ResourceListSearchParams.FilterModel];
  const nonAppliedFilterModelFromUrl =
    gridUrlState[ResourceListSearchParams.NotAppliedFilterModel];
  const logicOperator =
    gridUrlState[ResourceListSearchParams.FilterModelLogicOperator];

  return {
    filterModel: mapUrlFilterModelToGridFilterModel(
      appliedFilterModelFromUrl || {},
      logicOperator,
    ),
    notAppliedFilterModel: mapUrlFilterModelToGridFilterModel(
      nonAppliedFilterModelFromUrl || {},
      logicOperator,
    ),
    paginationModel,
    sortModel,
  };
};

const mapUrlFilterModelToGridFilterModel = (
  urlFilterModel: UrlFilterModel,
  logicOperator: GridLogicOperator | undefined,
) => {
  return {
    logicOperator: logicOperator,
    items: mapObjectToArray(urlFilterModel || {}, (value, key, index) => {
      return {
        field: key,
        id: index,
        value: value.v,
        operator: value.o,
      };
    }),
  };
};

const filterModelWithoutIds = (model: GridFilterModel) => {
  return {
    ...model,
    items: model.items.map(({ field, value, operator }) => ({
      field,
      value,
      operator,
    })),
  };
};
